import React from 'react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';

export function HowToPlay() {
  return (
    <div className="antialiased text-white selection:bg-gradient-to-r selection:from-yellow-400 selection:to-yellow-600 selection:text-black min-h-screen bg-black overflow-hidden">
      <Navbar />

      {/* Hero Section of How to Play */}
      <section className="relative py-32 overflow-hidden border-b-8 border-yellow-400">
        <div className="absolute top-0 right-0 w-3/4 h-full bg-halftone transform -skew-x-12 translate-x-1/4 z-0 opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col items-center text-center">
            <div className="bg-red-600 text-white px-6 py-2 transform -skew-x-12 border-2 border-white inline-block mb-8 rotate-2 shadow-[6px_6px_0px_#eab308]">
              <span className="font-anton text-xl tracking-widest uppercase transform skew-x-12 inline-block">🎓 THE ACADEMY</span>
            </div>
            <h1 className="font-anton text-6xl md:text-9xl uppercase leading-[0.85] text-shadow-hard transform -rotate-1 mb-8">
              HOW TO <span className="text-yellow-400">PLAY</span>
            </h1>
            <p className="font-marker text-2xl md:text-3xl text-gray-400 max-w-2xl transform rotate-1">
              Master the art of All Fives. Learn the rules, dominate the board, and steal the victory.
            </p>
          </div>
        </div>
      </section>

      {/* Core Rules Grid */}
      <section className="py-24 relative bg-white text-black border-b-8 border-black overflow-hidden">
        <div className="absolute inset-0 bg-halftone-white opacity-40 pointer-events-none"></div>
        <div className="container mx-auto px-4 relative z-10">

          {/* Objective */}
          <div className="mb-24 flex flex-col md:flex-row gap-12 items-center">
            <div className="flex-1 transform -rotate-2">
              <div className="bg-black text-white p-8 border-l-8 border-yellow-400 shadow-[10px_10px_0px_#ef4444]">
                <h2 className="font-anton text-4xl mb-4 uppercase tracking-tight">THE OBJECTIVE</h2>
                <p className="font-sans text-xl leading-relaxed text-gray-300">
                  Be the first player to reach the point limit (typically 150 or 200). Points are scored by making the ends of the domino chain add up to a <span className="text-yellow-400 font-bold">MULTIPLE OF FIVE.</span>
                </p>
              </div>
            </div>
            <div className="flex-1 font-marker text-4xl text-black transform rotate-3">
              "EVERY FIVE COUNTS. EVERY MOVE MATTERS."
            </div>
          </div>

          {/* Steps Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'THE DRAW',
                desc: "Each player draws 7 dominoes. The boneyard holds the rest. The player with the highest double starts the game.",
                color: 'bg-yellow-400'
              },
              {
                step: '02',
                title: 'THE MATCH',
                desc: "Match numbers on the ends of the chain. If you can't play, you must draw from the boneyard until you can.",
                color: 'bg-white'
              },
              {
                step: '03',
                title: 'THE SCORE',
                desc: "If the sum of all open ends is a multiple of 5 (5, 10, 15, 20...), you score that amount instantly.",
                color: 'bg-red-600',
                textColor: 'text-white'
              }
            ].map((item, i) => (
              <div key={i} className="group relative transform hover:-translate-y-2 transition-transform duration-300">
                <div className="absolute inset-0 bg-black translate-x-2 translate-y-2"></div>
                <div className={`relative ${item.color} border-4 border-black p-8 h-full ${item.textColor || 'text-black'}`}>
                  <div className="font-anton text-6xl opacity-20 mb-2">{item.step}</div>
                  <h3 className="font-anton text-3xl mb-4 uppercase">{item.title}</h3>
                  <p className="font-sans font-medium leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advanced Rules Section */}
      <section className="py-24 bg-neutral-900 border-b-8 border-yellow-400 relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="mb-16 flex items-center gap-4">
            <div className="h-1 w-24 bg-red-600"></div>
            <h2 className="font-anton text-5xl uppercase text-white">ADVANCED <span className="text-yellow-400">MECHANICS</span></h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* The Spinner */}
            <div className="bg-black border-2 border-gray-700 p-8 transform rotate-1 border-l-8 border-l-yellow-400">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-yellow-400 flex items-center justify-center">
                  <iconify-icon icon="solar:star-bold" width="32" color="#000"></iconify-icon>
                </div>
                <h3 className="font-anton text-3xl text-white">THE SPINNER</h3>
              </div>
              <p className="font-sans text-gray-400 text-lg leading-relaxed mb-6">
                The first double played becomes the <span className="text-white font-bold">SPINNER.</span> You can play off all four sides of this tile, creating more opportunities for scoring multiples of five.
              </p>
              <div className="bg-neutral-800 p-4 border border-gray-600 inline-block font-mono text-xs text-yellow-400">
                {`// STRATEGY TIP: Control the spinner to block opponents' high-point moves.`}
              </div>
            </div>

            {/* Going Out */}
            <div className="bg-black border-2 border-gray-700 p-8 transform -rotate-1 border-l-8 border-l-red-600">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-red-600 flex items-center justify-center">
                  <iconify-icon icon="solar:fire-bold" width="32" color="#fff"></iconify-icon>
                </div>
                <h3 className="font-anton text-3xl text-white">DOMINO!</h3>
              </div>
              <p className="font-sans text-gray-400 text-lg leading-relaxed mb-6">
                When a player plays their last tile, they "Domino" and win the hand. They score the total sum of all tiles left in opponents' hands, rounded to the nearest five.
              </p>
              <div className="bg-neutral-800 p-4 border border-gray-600 inline-block font-mono text-xs text-red-500">
                {`// PRO TIP: Don't get caught with high-value tiles in your hand!`}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-yellow-400 text-black text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-halftone opacity-10 pointer-events-none"></div>
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="font-anton text-6xl md:text-8xl uppercase mb-8 transform -rotate-2">
            READY TO <span className="bg-black text-white px-4">WIN?</span>
          </h2>
          <a
            href="/"
            className="relative inline-block group"
          >
            <div className="absolute inset-0 bg-black translate-x-2 translate-y-2 transform transition-transform group-hover:translate-x-3 group-hover:translate-y-3"></div>
            <div className="relative bg-white border-4 border-black px-12 py-6 font-anton text-4xl uppercase tracking-wider transition-colors group-hover:bg-red-600 group-hover:text-white">
              PLAY NOW
            </div>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}